import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { FileText, Search, Download, FileSpreadsheet, Loader2, ExternalLink, User, Building2 } from 'lucide-react';

const eur = (n) => `${Number(n || 0).toFixed(2)} €`;

export const InvoicesList = () => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = async (term = '') => {
    setLoading(true);
    try {
      const { data } = await api.get('/receipts/admin/facturas', { params: term ? { q: term } : {} });
      setItems(data || []);
    } catch (e) {
      toast.error(t('invoices.loadError', { defaultValue: 'Errore nel caricamento delle fatture' }));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Ricerca con debounce.
  useEffect(() => {
    const id = setTimeout(() => load(q.trim()), 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q]);

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(i18n.language); } catch { return iso || ''; } };

  const openPdf = async (f) => {
    try {
      const res = await api.get(`/receipts/admin/facturas/${f.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(t('invoices.pdfError', { defaultValue: 'Impossibile aprire il PDF' }));
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/receipts/admin/facturas/export.csv', { params: q.trim() ? { q: q.trim() } : {}, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'facturas.csv'; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('invoices.exportError', { defaultValue: 'Esportazione non riuscita' }));
    } finally { setExporting(false); }
  };

  const totalSum = useMemo(() => items.reduce((s, f) => s + Number(f.total || 0), 0), [items]);

  return (
    <Card className="p-5" data-testid="invoices-list-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><FileText className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold leading-tight">{t('invoices.title', { defaultValue: 'Fatture generate' })}</h3>
            <p className="text-sm text-muted-foreground">{t('invoices.subtitle', { defaultValue: 'Tutte le fatture richieste dai clienti. Cerca ed esporta per la contabilità.' })}</p>
          </div>
        </div>
        <Button onClick={exportCsv} disabled={exporting || items.length === 0} variant="outline" className="rounded-xl" data-testid="invoices-export-button">
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
          {t('invoices.exportCsv', { defaultValue: 'Esporta CSV' })}
        </Button>
      </div>

      <div className="relative mb-4 sm:max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('invoices.search', { defaultValue: 'Cerca per numero, nome o NIF/CIF…' })} className="pl-9" data-testid="invoices-search-input" />
      </div>

      {loading ? (
        <div className="py-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground" data-testid="invoices-empty">
          <FileText className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-3">{t('invoices.empty', { defaultValue: 'Nessuna fattura generata.' })}</p>
        </div>
      ) : (<>
        {/* Desktop table */}
        <div className="hidden md:block overflow-hidden rounded-xl border border-border">
          <Table data-testid="invoices-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('invoices.colNumber', { defaultValue: 'Numero' })}</TableHead>
                <TableHead>{t('invoices.colDate', { defaultValue: 'Data' })}</TableHead>
                <TableHead>{t('invoices.colClient', { defaultValue: 'Cliente' })}</TableHead>
                <TableHead className="text-right">{t('invoices.colTaxable', { defaultValue: 'Imponibile' })}</TableHead>
                <TableHead className="text-right">{t('invoices.colVat', { defaultValue: 'IVA' })}</TableHead>
                <TableHead className="text-right">{t('invoices.colTotal', { defaultValue: 'Totale' })}</TableHead>
                <TableHead className="text-right">{t('invoices.colActions', { defaultValue: 'Azioni' })}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((f) => (
                <TableRow key={f.id} data-testid={`invoice-row-${f.numero}`}>
                  <TableCell className="font-mono text-sm font-medium">{f.numero}</TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(f.created_at)}</TableCell>
                  <TableCell className="text-sm">
                    <span className="font-medium flex items-center gap-1.5">
                      {f.receptor?.tipo === 'empresa' ? <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> : <User className="h-3.5 w-3.5 text-muted-foreground" />}
                      {f.receptor?.nombre || '—'}
                    </span>
                    {f.receptor?.nif && <span className="text-xs text-muted-foreground font-mono">{f.receptor.nif}</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{eur(f.imponibile)}</TableCell>
                  <TableCell className="text-right tabular-nums">{eur(f.iva)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{eur(f.total)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => openPdf(f)} data-testid={`invoice-pdf-${f.numero}`}>
                      <Download className="h-4 w-4 mr-1.5" /> PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden flex flex-col gap-3" data-testid="invoices-card-list">
          {items.map((f) => (
            <div key={f.id} className="rounded-xl border border-border p-4" data-testid={`invoice-card-${f.numero}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold">{f.numero}</span>
                <span className="font-semibold tabular-nums">{eur(f.total)}</span>
              </div>
              <div className="mt-1 text-sm flex items-center gap-1.5">
                {f.receptor?.tipo === 'empresa' ? <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> : <User className="h-3.5 w-3.5 text-muted-foreground" />}
                {f.receptor?.nombre || '—'} {f.receptor?.nif ? <span className="text-xs text-muted-foreground font-mono">· {f.receptor.nif}</span> : null}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(f.created_at)}</div>
              <div className="mt-3">
                <Button variant="outline" size="sm" className="rounded-lg w-full" onClick={() => openPdf(f)} data-testid={`invoice-pdf-mobile-${f.numero}`}>
                  <ExternalLink className="h-4 w-4 mr-1.5" /> {t('invoices.openPdf', { defaultValue: 'Apri PDF' })}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end text-sm text-muted-foreground">
          {t('invoices.totalCount', { count: items.length, defaultValue: `${items.length} fatture` })} · <span className="ml-1 font-semibold text-foreground">{eur(totalSum)}</span>
        </div>
      </>)}
    </Card>
  );
};
